import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import type { WorkItem } from '../../types/models';

const SHOWN = 5;

/**
 * "Still doing these?" Items carried forward for more than two weeks, oldest
 * first, each with a decision: keep it (its carry-over clock restarts) or drop
 * it. Delegating, or anything else, happens on the item itself.
 */
export function StillDoingList({ items, busyIds, onKeep, onDrop, onOpenAll }: {
  items: WorkItem[];
  busyIds: ReadonlySet<string>;
  onKeep: (item: WorkItem) => void;
  onDrop: (item: WorkItem) => void;
  onOpenAll: () => void;
}) {
  if (items.length === 0) return null;
  const shown = items.slice(0, SHOWN);
  const more = items.length - shown.length;
  return (
    <section aria-labelledby="still-doing-title">
      <h3 id="still-doing-title" className="text-sm font-semibold">Still doing these?</h3>
      <p className="mb-2 text-xs text-muted-foreground">
        {items.length === 1
          ? '1 item has rolled over for two weeks or more.'
          : `${items.length} items have rolled over for two weeks or more.`}{' '}
        They're out of today's list until you decide.
      </p>
      <ul className="flex flex-col gap-2">
        {shown.map(item => (
          <li key={item.id} className="rounded-lg border border-border p-2.5">
            <p className="text-sm leading-snug">{item.title}</p>
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {item.originalDate ? `Since ${format(new Date(`${item.originalDate.slice(0, 10)}T12:00:00`), 'd MMM')}` : ''}
              </span>
              <span className="flex gap-1.5">
                <Button size="sm" variant="outline" className="h-7" disabled={busyIds.has(item.id)}
                  aria-label={`Still doing it: ${item.title}`}
                  onClick={() => onKeep(item)}>
                  Still doing it
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-muted-foreground" disabled={busyIds.has(item.id)}
                  aria-label={`Drop: ${item.title}`}
                  onClick={() => onDrop(item)}>
                  Drop
                </Button>
              </span>
            </div>
          </li>
        ))}
      </ul>
      {more > 0 && (
        <Button variant="link" size="sm" className="mt-1 h-auto px-0" onClick={onOpenAll}>
          {more} more in Work Items
        </Button>
      )}
    </section>
  );
}
